/*
 * LandingPage — the entry chooser.
 *
 * Two doors: the manual Console, or Ada. Rendered in place of both (see
 * index.js), so picking one is a pushState away rather than a page load.
 *
 * The hover behaviour is the point of the layout: a connector line draws
 * downward out of the card and the brief expands under it, so the explanation
 * arrives attached to the thing it explains rather than floating in a tooltip.
 * It is driven by :hover AND :focus-within, so tabbing through reveals the same
 * detail a mouse does.
 */

import React from "react";
import {
  ASSISTANT_NAME, CONSOLE_NAME, VIEW_CONSOLE, VIEW_ASSISTANT, navigateTo,
} from "../appView";

const OPTIONS = [
  {
    view: VIEW_CONSOLE,
    accent: "cyan",
    kicker: "DIRECT CONTROL",
    name: CONSOLE_NAME,
    tagline: "Every panel, every metric, every switch",
    desc:
      "The full manual surface. Read live link health, per-class traffic and tunnel " +
      "diagnostics, then change policy, QoS, bonding and encryption yourself.",
    points: ["Live WAN + traffic telemetry", "Policy, QoS and AQM editors", "Diagnostics, AS-path, remote shell"],
  },
  {
    view: VIEW_ASSISTANT,
    accent: "violet",
    kicker: "NETWORK INTELLIGENCE",
    name: ASSISTANT_NAME,
    tagline: "Ask the network what is wrong",
    desc:
      `Describe the problem in plain language. ${ASSISTANT_NAME} reads this appliance's own ` +
      "telemetry, works out what is happening, and drafts the change for you to approve.",
    points: ["Reads live state directly", "Explains what it found, and how", "Proposes changes — you approve them"],
  },
];

export default function LandingPage() {
  return (
    <div className="lp-root">
      <Styles />
      <div className="lp-grid" aria-hidden="true" />
      <div className="lp-glow" aria-hidden="true" />
      <div className="lp-scan" aria-hidden="true" />

      <header className="lp-head">
        <span className="lp-wordmark">WEAVIAN</span>
        <span className="lp-rule" aria-hidden="true" />
        <span className="lp-meta">EDGE&nbsp;/&nbsp;MULTI-WAN&nbsp;FABRIC</span>
      </header>

      <main className="lp-main">
        <img
          className="lp-logo"
          src={`${process.env.PUBLIC_URL}/sidebar-logo-w.png`}
          alt="Weavian"
          width="96"
          height="96"
        />
        <p className="lp-eyebrow">SELECT INTERFACE</p>
        <h1 className="lp-title">
          How do you want to <span className="lp-title-accent">drive the network</span>?
        </h1>

        <div className="lp-options">
          {OPTIONS.map((o) => <Option key={o.view} option={o} />)}
        </div>
      </main>

      <footer className="lp-foot">
        <span>You can move between the two at any time</span>
      </footer>
    </div>
  );
}

function Option({ option }) {
  const go = () => navigateTo(option.view);

  return (
    <div className={`lp-slot lp-${option.accent}`}>
      <button className="lp-card" onClick={go} aria-describedby={`lp-desc-${option.view}`}>
        <i className="lp-corner lp-tl" aria-hidden="true" />
        <i className="lp-corner lp-tr" aria-hidden="true" />
        <i className="lp-corner lp-bl" aria-hidden="true" />
        <i className="lp-corner lp-br" aria-hidden="true" />
        <span className="lp-kicker">{option.kicker}</span>
        <span className="lp-name">{option.name}</span>
        <span className="lp-tagline">{option.tagline}</span>
        <span className="lp-enter">ENTER<em>&#8594;</em></span>
      </button>

      {/* Connector: draws downward on hover, then the brief opens under it. */}
      <svg className="lp-connector" viewBox="0 0 2 44" preserveAspectRatio="none" aria-hidden="true">
        <line x1="1" y1="0" x2="1" y2="44" />
      </svg>
      <span className="lp-node" aria-hidden="true" />

      <div className="lp-desc" id={`lp-desc-${option.view}`} role="note">
        <p>{option.desc}</p>
        <ul>
          {option.points.map((p) => <li key={p}>{p}</li>)}
        </ul>
      </div>
    </div>
  );
}

function Styles() {
  return (
    <style>{`
.lp-root{
  --bg:#05060A; --fg:#E6E9F2; --dim:#7C8397; --line:rgba(255,255,255,.10);
  --cyan:#22D3EE; --violet:#A855F7;
  position:fixed;inset:0;background:var(--bg);color:var(--fg);overflow:auto;
  font-family:'Inter',ui-sans-serif,system-ui,-apple-system,sans-serif;
  display:flex;flex-direction:column;
}
.lp-root *{box-sizing:border-box}
.lp-root button{font:inherit}

/* Grid + glow + scanlines: the cyberpunk substrate, all non-interactive. */
.lp-grid{
  position:absolute;inset:0;pointer-events:none;
  background-image:
    linear-gradient(rgba(120,140,190,.055) 1px,transparent 1px),
    linear-gradient(90deg,rgba(120,140,190,.055) 1px,transparent 1px);
  background-size:52px 52px;
  mask-image:radial-gradient(ellipse 78% 62% at 50% 42%,#000 32%,transparent 78%);
  -webkit-mask-image:radial-gradient(ellipse 78% 62% at 50% 42%,#000 32%,transparent 78%);
}
.lp-glow{
  position:absolute;left:50%;top:44%;transform:translate(-50%,-50%);
  width:130vw;height:80vh;pointer-events:none;
  background:
    radial-gradient(closest-side,rgba(34,211,238,.13),transparent 70%),
    radial-gradient(closest-side,rgba(168,85,247,.15),transparent 66%);
  filter:blur(50px);
}
.lp-scan{
  position:absolute;inset:0;pointer-events:none;opacity:.30;
  background:repeating-linear-gradient(180deg,rgba(255,255,255,.035) 0 1px,transparent 1px 3px);
}

/* Header */
.lp-head{
  position:relative;z-index:2;display:flex;align-items:center;gap:12px;
  padding:20px 28px;border-bottom:1px solid var(--line);
}
.lp-wordmark{
  font-size:22px;font-weight:700;letter-spacing:.34em;
  font-family:ui-monospace,SFMono-Regular,Menlo,monospace;
}
.lp-rule{flex:1;height:1px;background:linear-gradient(90deg,var(--line),transparent)}
.lp-meta{
  font-size:16px;letter-spacing:.22em;color:var(--dim);
  font-family:ui-monospace,SFMono-Regular,Menlo,monospace;
}

/* Hero logo. The glow sits behind it rather than on it so the mark keeps its
   own colour instead of being tinted by the accent gradient. */
.lp-logo{
  width:clamp(112px,10.5vw,168px);height:auto;display:block;margin-bottom:18px;
  filter:drop-shadow(0 0 26px rgba(168,85,247,.45)) drop-shadow(0 0 12px rgba(34,211,238,.30));
  animation:lpfloat 7s ease-in-out infinite;
}
@keyframes lpfloat{0%,100%{transform:translateY(0)}50%{transform:translateY(-6px)}}

/* Main */
.lp-main{
  position:relative;z-index:2;flex:1;display:flex;flex-direction:column;
  align-items:center;justify-content:center;padding:40px 24px 24px;gap:6px;
}
.lp-eyebrow{
  margin:0;font-family:ui-monospace,SFMono-Regular,Menlo,monospace;
  font-size:18px;letter-spacing:.34em;color:var(--dim);
}
.lp-eyebrow::before{content:"// "}
.lp-title{
  margin:14px 0 52px;text-align:center;font-weight:600;letter-spacing:-.03em;
  font-size:clamp(58px,7.8vw,104px);line-height:1.06;
}
.lp-title-accent{
  background:linear-gradient(100deg,var(--cyan),var(--violet));
  -webkit-background-clip:text;background-clip:text;color:transparent;
}

/* Option row. align-items:start so one slot expanding never shifts the other. */
.lp-options{
  display:flex;gap:42px;align-items:flex-start;justify-content:center;
  flex-wrap:wrap;width:min(1240px,100%);
}
.lp-slot{
  --accent:var(--cyan);
  position:relative;display:flex;flex-direction:column;align-items:center;
  width:min(560px,92vw);
}
.lp-violet{--accent:var(--violet)}

/* Card */
.lp-card{
  position:relative;width:100%;text-align:left;cursor:pointer;
  display:flex;flex-direction:column;gap:10px;
  padding:46px 40px 36px;border-radius:3px;color:inherit;
  background:linear-gradient(160deg,rgba(255,255,255,.045),rgba(255,255,255,.014));
  border:1px solid var(--line);
  transition:border-color .28s,background .28s,transform .28s,box-shadow .28s;
}
.lp-slot:hover .lp-card,.lp-slot:focus-within .lp-card{
  border-color:color-mix(in srgb,var(--accent) 55%,transparent);
  background:linear-gradient(160deg,
    color-mix(in srgb,var(--accent) 13%,transparent),rgba(255,255,255,.02));
  transform:translateY(-3px);
  box-shadow:0 20px 50px -22px color-mix(in srgb,var(--accent) 75%,transparent);
}
.lp-card:focus-visible{outline:2px solid var(--accent);outline-offset:3px}

/* Corner brackets */
.lp-corner{
  position:absolute;width:12px;height:12px;opacity:.34;
  border-color:var(--accent);border-style:solid;border-width:0;
  transition:opacity .28s,width .28s,height .28s;
}
.lp-tl{top:-1px;left:-1px;border-top-width:1.5px;border-left-width:1.5px}
.lp-tr{top:-1px;right:-1px;border-top-width:1.5px;border-right-width:1.5px}
.lp-bl{bottom:-1px;left:-1px;border-bottom-width:1.5px;border-left-width:1.5px}
.lp-br{bottom:-1px;right:-1px;border-bottom-width:1.5px;border-right-width:1.5px}
.lp-slot:hover .lp-corner,.lp-slot:focus-within .lp-corner{opacity:1;width:20px;height:20px}

.lp-kicker{
  font-family:ui-monospace,SFMono-Regular,Menlo,monospace;
  font-size:16px;letter-spacing:.26em;color:var(--accent);opacity:.85;
}
.lp-name{font-size:clamp(52px,5vw,74px);font-weight:600;letter-spacing:-.025em;line-height:1.06}
.lp-tagline{font-size:22px;color:var(--dim);line-height:1.42}
.lp-enter{
  margin-top:12px;display:inline-flex;align-items:center;gap:8px;
  font-family:ui-monospace,SFMono-Regular,Menlo,monospace;
  font-size:16px;letter-spacing:.2em;color:var(--accent);
  opacity:.55;transition:opacity .28s;
}
.lp-enter em{font-style:normal;transition:transform .28s}
.lp-slot:hover .lp-enter,.lp-slot:focus-within .lp-enter{opacity:1}
.lp-slot:hover .lp-enter em,.lp-slot:focus-within .lp-enter em{transform:translateX(5px)}

/* Connector — draws out of the card on hover, then the brief opens beneath. */
.lp-connector{width:2px;height:44px;overflow:visible;display:block}
.lp-connector line{
  stroke:var(--accent);stroke-width:1.4;
  stroke-dasharray:44;stroke-dashoffset:44;
  transition:stroke-dashoffset .34s cubic-bezier(.4,0,.2,1);
}
.lp-slot:hover .lp-connector line,.lp-slot:focus-within .lp-connector line{stroke-dashoffset:0}
.lp-node{
  width:5px;height:5px;border-radius:50%;background:var(--accent);
  opacity:0;transform:scale(.2);margin-bottom:-2.5px;
  transition:opacity .2s .3s,transform .2s .3s;
}
.lp-slot:hover .lp-node,.lp-slot:focus-within .lp-node{opacity:1;transform:scale(1)}

.lp-desc{
  width:100%;overflow:hidden;
  max-height:0;opacity:0;transform:translateY(-6px);
  border:1px solid transparent;border-radius:3px;
  transition:max-height .38s cubic-bezier(.4,0,.2,1),opacity .28s .08s,
             transform .34s .06s,border-color .3s .08s,padding .38s;
  padding:0 16px;
  background:linear-gradient(180deg,
    color-mix(in srgb,var(--accent) 9%,transparent),rgba(255,255,255,.02));
}
.lp-slot:hover .lp-desc,.lp-slot:focus-within .lp-desc{
  max-height:600px;opacity:1;transform:translateY(0);padding:26px 26px;
  border-color:color-mix(in srgb,var(--accent) 34%,transparent);
}
.lp-desc p{margin:0 0 12px;font-size:20px;line-height:1.55;color:#C4CAD9}
.lp-desc ul{margin:0;padding:0;list-style:none;display:flex;flex-direction:column;gap:7px}
.lp-desc li{
  font-family:ui-monospace,SFMono-Regular,Menlo,monospace;
  font-size:16.5px;color:var(--dim);padding-left:24px;position:relative;
}
.lp-desc li::before{content:"▸";position:absolute;left:0;color:var(--accent);opacity:.75}

/* Footer */
.lp-foot{
  position:relative;z-index:2;display:flex;align-items:center;gap:10px;
  justify-content:center;padding:20px;border-top:1px solid var(--line);
  font-family:ui-monospace,SFMono-Regular,Menlo,monospace;
  font-size:16px;letter-spacing:.16em;color:var(--dim);
}

@media (max-width:860px){
  .lp-options{gap:26px}
  .lp-title{margin-bottom:28px}
}
@media (prefers-reduced-motion:reduce){
  .lp-root *,.lp-root *::before,.lp-root *::after{
    animation-duration:.001ms!important;transition-duration:.001ms!important;
  }
}
`}</style>
  );
}
